import React, { useMemo } from 'react'
import { motion } from 'framer-motion'
import { usePuzzle } from './PuzzleContext'

interface PuzzleLineProps {
  lineId: string
}

export const PuzzleLine: React.FC<PuzzleLineProps> = ({ lineId }) => {
  const { topology, values, lineStatus } = usePuzzle()
  const line = topology.lines.find(l => l.id === lineId)
  
  if (!line) return null
  
  const status = lineStatus[lineId]
  const isFull = status?.isFull || false
  const isValid = status?.isValid || false
  const currentSum = status?.sum || 0
  const targetSum = line.targetSum

  const pathData = useMemo(() => {
    // Get coordinates of all nodes in this line
    const points = line.nodeIds.map(nodeId => {
      const node = topology.nodes.find(n => n.id === nodeId)
      return node ? { x: node.x, y: node.y } : null
    }).filter(Boolean) as { x: number, y: number }[]

    if (points.length < 2) return ''

    if (line.type === 'circle') {
      // Calculate center of all points
      const cx = points.reduce((sum, p) => sum + p.x, 0) / points.length
      const cy = points.reduce((sum, p) => sum + p.y, 0) / points.length
      
      // Calculate average radius
      const r = points.reduce((sum, p) => {
        const dx = p.x - cx
        const dy = p.y - cy
        return sum + Math.sqrt(dx * dx + dy * dy)
      }, 0) / points.length

      // Draw circle
      // A rx ry x-axis-rotation large-arc-flag sweep-flag x y
      // But for full circle, we can use M cx-r cy A r r 0 1 1 cx+r cy A r r 0 1 1 cx-r cy
      // Or simply return circle path data
      return `M ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${cx + r} ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy}`
    }

    // Default: Polyline
    // Map percentage coordinates (0-100) to SVG viewbox coordinates (e.g., 0-100 or 0-1000)
    // Assuming parent SVG uses viewBox="0 0 100 100"
    return `M ${points.map(p => `${p.x} ${p.y}`).join(' L ')}`
  }, [line, topology.nodes])

  // Determine stroke color
  let strokeColor = '#e2e8f0' // slate-200
  let strokeWidth = 2
  
  if (isFull) {
    if (targetSum !== undefined) {
      // green-500 : red-400
      strokeColor = currentSum === targetSum ? '#22c55e' : '#f87171' 
      strokeWidth = 4
    } else {
      // blue-500 (just filled, no target)
      strokeColor = '#3b82f6' 
    }
  }

  return (
    <motion.path
      d={pathData}
      fill="none"
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      initial={{ pathLength: 0 }}
      animate={{ 
        pathLength: 1,
        stroke: strokeColor,
        strokeWidth: strokeWidth
      }}
      transition={{ duration: 0.5 }}
    />
  )
}
